import { toContentLocale } from "@/i18n/config";

/**
 * Fallback copy for the SEO block labels. The Lokalise keys (namespace `listing`) still
 * need to be created; until then `t(key, fallback)` renders these. Same pattern as the
 * heading in the first version of the block. No em or en dashes.
 */

type LocaleCopy = Record<string, string>;

const pick = (copy: LocaleCopy, locale: string | undefined): string =>
  copy[toContentLocale(locale)] ?? copy.en ?? "";

const GUIDES_HEADING: LocaleCopy = {
  en: "Guides and inspiration",
  nl: "Gidsen en inspiratie",
  de: "Ratgeber und Inspiration",
  fr: "Guides et inspiration",
  es: "Guías e inspiración",
  it: "Guide e ispirazione",
};

const VIEW_ALL_GUIDES: LocaleCopy = {
  en: "View all guides",
  nl: "Bekijk alle gidsen",
  de: "Alle Ratgeber ansehen",
  fr: "Voir tous les guides",
  es: "Ver todas las guías",
  it: "Vedi tutte le guide",
};

const MIN_READ: LocaleCopy = {
  en: "{{minutes}} min read",
  nl: "{{minutes}} min lezen",
  de: "{{minutes}} Min. Lesezeit",
  fr: "{{minutes}} min de lecture",
  es: "{{minutes}} min de lectura",
  it: "{{minutes}} min di lettura",
};

const RELATED_SEARCHES_HEADING: LocaleCopy = {
  en: "Related searches",
  nl: "Gerelateerde zoekopdrachten",
  de: "Ähnliche Suchen",
  fr: "Recherches associées",
  es: "Búsquedas relacionadas",
  it: "Ricerche correlate",
};

const DISCOVER_MORE_HEADING: LocaleCopy = {
  en: "Discover more",
  nl: "Ontdek meer",
  de: "Mehr entdecken",
  fr: "Découvrir plus",
  es: "Descubre más",
  it: "Scopri di più",
};

const ICON_GUIDES_HEADING: LocaleCopy = {
  en: "Price and authenticity guides",
  nl: "Prijs- en echtheidsgidsen",
  de: "Preis- und Echtheitsratgeber",
  fr: "Guides de prix et d'authenticité",
  es: "Guías de precio y autenticidad",
  it: "Guide a prezzi e autenticità",
};

export const PLP_SEO_KEYS = {
  guidesHeading: "listing:plp-editorial-links-heading",
  viewAllGuides: "listing:plp-seo-view-all-guides",
  minRead: "listing:plp-seo-min-read",
  relatedSearchesHeading: "listing:plp-seo-related-searches-heading",
  discoverMoreHeading: "listing:plp-seo-discover-more-heading",
  iconGuidesHeading: "listing:plp-seo-icon-guides-heading",
  readMore: "common:read-more",
  showLess: "common:common-show-less",
  faqHeading: "common:common-faq-title",
} as const;

export const plpSeoFallback = {
  guidesHeading: (locale?: string) => pick(GUIDES_HEADING, locale),
  viewAllGuides: (locale?: string) => pick(VIEW_ALL_GUIDES, locale),
  minRead: (locale?: string) => pick(MIN_READ, locale),
  relatedSearchesHeading: (locale?: string) =>
    pick(RELATED_SEARCHES_HEADING, locale),
  discoverMoreHeading: (locale?: string) => pick(DISCOVER_MORE_HEADING, locale),
  iconGuidesHeading: (locale?: string) => pick(ICON_GUIDES_HEADING, locale),
};
